import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { LayoutData, ProductSummary } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { Breadcrumbs } from '@/components/generals/breadcrumbs'
import { SectionHeading } from '@/components/generals/section-heading'
import { CtaBanner } from '@/components/generals/cta-banner'
import { CatalogView } from '@/components/catalog/catalog-view'
import { getLayoutData, getProducts } from '@/lib/catalog'

interface ProductsPageProps {
    products: ProductSummary[]
    layout: LayoutData
    unavailable: boolean
}

const ProductsPage: FC<ProductsPageProps> = ({ products, layout, unavailable }): ReactNode => {
    return (
        <Layout categories={layout.categories}>
            <Seo
                title='محصولات'
                description='کاتالوگ کامل مبلمان نارون: کاناپه و مبل راحتی، مبل تک، ناهارخوری، اتاق خواب، میز و بوفه؛ همه با امکان سفارش در ابعاد، پارچه و رنگ دلخواه.'
                ogImage={layout.shareImage}
            />

            <section aria-labelledby='catalog-title' className='pb-20 pt-8 lg:pb-24 lg:pt-12'>
                <Container>
                    <Breadcrumbs
                        items={[
                            { key: 'home', label: 'خانه', href: '/' },
                            { key: 'products', label: 'محصولات' }
                        ]}
                    />

                    <SectionHeading
                        as='h1'
                        eyebrow='کاتالوگ نارون'
                        title='همهٔ محصولات'
                        titleId='catalog-title'
                        description='هر محصول را می‌توانید با رنگ، پارچه و ابعاد دلخواه سفارش دهید. برای دیدن جزئیات و ثبت درخواست، روی هر محصول بزنید.'
                        className='mt-8'
                    />

                    <div className='mt-12'>
                        <CatalogView products={products} categories={layout.categories} unavailable={unavailable} />
                    </div>
                </Container>
            </section>

            <CtaBanner />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<ProductsPageProps> = async () => {
    try {
        const [products, layout] = await Promise.all([getProducts(), getLayoutData()])

        return {
            props: { products, layout, unavailable: false },
            revalidate: 60
        }
    }
    catch (error) {
        console.error('Products could not be loaded:', error instanceof Error ? error.message : error)

        return {
            props: { products: [], layout: await getLayoutData([]), unavailable: true },
            revalidate: 30
        }
    }
}

export default ProductsPage
